import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React from 'react';

import { DiffViewProps } from './types';
import { formatDiff } from './utils';

const DiffView: React.FC<DiffViewProps> = ({ diff }) => {
  const getSummaryMessage = () => {
    const hasLineDifferences = diff.lines.some((line) => line.hasDifference);

    if (diff.debitDiff === 0 && diff.creditDiff === 0) {
      if (hasLineDifferences) {
        return 'Hay líneas modificadas, aunque los totales cuadran';
      }
      return null;
    }

    const parts: string[] = [];
    if (diff.debitDiff !== 0) {
      const sign = diff.debitDiff > 0 ? '+' : '';
      parts.push(`${sign}${formatMoney(diff.debitDiff)} en Debe`);
    }
    if (diff.creditDiff !== 0) {
      const sign = diff.creditDiff > 0 ? '+' : '';
      parts.push(`${sign}${formatMoney(diff.creditDiff)} en Haber`);
    }
    return `No cuadra por ${parts.join(' y ')}`;
  };

  const summaryMessage = getSummaryMessage();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          mb: 1.5,
          p: 1,
          borderRadius: 1,
          bgcolor: summaryMessage ? '#fff3e0' : '#e8f5e9',
          border: summaryMessage ? '1px solid #ffb74d' : '1px solid #81c784',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: summaryMessage ? '#e65100' : '#2e7d32',
          }}
        >
          {summaryMessage ||
            'Sin diferencias - El asiento cuadra correctamente'}
        </Typography>
      </Box>

      <TableContainer sx={{ flex: 1, maxHeight: 280, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  bgcolor: `${BASE_COLORS.softGray}40`,
                }}
              >
                Cuenta
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                Nombre
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                Doc
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 'bold',
                  bgcolor: `${BASE_COLORS.softGray}40`,
                }}
              >
                Debe
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 'bold',
                  bgcolor: `${BASE_COLORS.softGray}40`,
                }}
              >
                Haber
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {diff.lines.map((line, idx) => (
              <TableRow
                key={idx}
                sx={{ bgcolor: line.hasDifference ? '#fff8e1' : 'inherit' }}
              >
                <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                  {line.accountCode}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '0.75rem',
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={line.accountName}
                >
                  {line.accountName}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>
                  {line.documentKey || '-'}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                  <Box>
                    <span>
                      {line.calculatedDebit !== null
                        ? formatMoney(line.calculatedDebit)
                        : '-'}
                    </span>
                    {line.debitDiff !== 0 && (
                      <Typography
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: '0.65rem',
                          color:
                            line.debitDiff > 0
                              ? BASE_COLORS.green
                              : BASE_COLORS.red,
                          fontWeight: 'bold',
                        }}
                      >
                        {formatDiff(line.debitDiff)}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                  <Box>
                    <span>
                      {line.calculatedCredit !== null
                        ? formatMoney(line.calculatedCredit)
                        : '-'}
                    </span>
                    {line.creditDiff !== 0 && (
                      <Typography
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: '0.65rem',
                          color:
                            line.creditDiff > 0
                              ? BASE_COLORS.green
                              : BASE_COLORS.red,
                          fontWeight: 'bold',
                        }}
                      >
                        {formatDiff(line.creditDiff)}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          mt: 1,
          pt: 1,
          borderTop: '2px solid #0288d1',
        }}
      >
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  border: 0,
                  py: 0.5,
                }}
              >
                Transaccional:
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  border: 0,
                  py: 0.5,
                }}
              >
                {formatMoney(diff.calculatedTotalDebit)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  border: 0,
                  py: 0.5,
                }}
              >
                {formatMoney(diff.calculatedTotalCredit)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                sx={{ fontSize: '0.8rem', color: '#666', border: 0, py: 0.5 }}
              >
                Guardado:
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontSize: '0.8rem', color: '#666', border: 0, py: 0.5 }}
              >
                {formatMoney(diff.savedTotalDebit)}
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontSize: '0.8rem', color: '#666', border: 0, py: 0.5 }}
              >
                {formatMoney(diff.savedTotalCredit)}
              </TableCell>
            </TableRow>
            {(diff.debitDiff !== 0 || diff.creditDiff !== 0) && (
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    color: '#e65100',
                    border: 0,
                    py: 0.5,
                  }}
                >
                  Diferencia:
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    color:
                      diff.debitDiff > 0
                        ? BASE_COLORS.green
                        : diff.debitDiff < 0
                          ? BASE_COLORS.red
                          : '#666',
                    border: 0,
                    py: 0.5,
                  }}
                >
                  {diff.debitDiff !== 0
                    ? `${diff.debitDiff > 0 ? '+' : ''}${formatMoney(diff.debitDiff)}`
                    : '-'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    color:
                      diff.creditDiff > 0
                        ? BASE_COLORS.green
                        : diff.creditDiff < 0
                          ? BASE_COLORS.red
                          : '#666',
                    border: 0,
                    py: 0.5,
                  }}
                >
                  {diff.creditDiff !== 0
                    ? `${diff.creditDiff > 0 ? '+' : ''}${formatMoney(diff.creditDiff)}`
                    : '-'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default DiffView;
